import { useEditorClass } from '@tunasong/editor'
import { graphHooks } from '@tunasong/graph-lib/react'
import { ExpandLess, ExpandMore } from '@tunasong/icons'
import type { Comment, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import { useId, useState } from 'react'
import ChatFeedItem from './chat-feed-item.js'
import { CommentMenu } from './comment-menu.js'
import { CommentContent } from './comment-content.js'
import styles from './task-list.module.css'

/** Where an anchored task sits in the song: its group's heading, and the block it opens */
export interface TaskAnchor {
  /** The group heading: "On Verse 2", "On “Walking down”" */
  heading: string
  /** The block, a lyric in quotes, for Show in song's name */
  label: string
  onShowInSong(): void
}

export interface TaskEntry {
  /** A song-level task is whole; an anchored one may carry only what ordered it, and loads the rest */
  comment: Persisted<Comment>
  anchor?: TaskAnchor
}

export interface TaskListProps {
  tasks: TaskEntry[]
}

const TaskItem: FC<TaskEntry> = ({ comment: given, anchor }) => {
  const ChatEditor = useEditorClass('ChatEditor')
  const headingId = useId()
  /** The anchored thread's batch read has no body; the row reads the whole comment, live */
  const { entity } = graphHooks.useEntity<Persisted<Comment>>(given.children ? undefined : given.id)
  const comment = given.children ? given : entity
  if (!comment) {
    return null
  }
  return (
    <li className={styles.item} aria-labelledby={anchor ? headingId : undefined}>
      {anchor ? (
        <div className={styles.anchor}>
          <span id={headingId} className={styles.heading} title={anchor.heading}>
            {anchor.heading}
          </span>
          <button
            type="button"
            className={styles.show}
            aria-label={`Show in song: ${anchor.label}`}
            onClick={anchor.onShowInSong}
          >
            Show in song
          </button>
        </div>
      ) : null}
      <ChatFeedItem
        ref={null}
        item={{
          ...comment,
          content: (
            <CommentContent comment={comment}>
              <ChatEditor readOnly={true} autoFocus={false} element={comment} />
            </CommentContent>
          ),
          actions: <CommentMenu comment={comment} />,
        }}
      />
    </li>
  )
}

const byCreated = (a: TaskEntry, b: TaskEntry) => (a.comment.createdAt ?? '').localeCompare(b.comment.createdAt ?? '')

/**
 * The Tasks filter (chat tasks M2.4, GH #3041): this entity's tasks, song-level and anchored. Open ones first, oldest
 * first so the longest-waiting is on top, then the done ones under a collapsed "Done ({n})" (T11). With none, T14.
 */
export const TaskList: FC<TaskListProps> = ({ tasks }) => {
  const [showDone, setShowDone] = useState(false)
  const doneId = useId()

  if (tasks.length === 0) {
    return <p className={styles.empty}>No tasks. Mark a comment as a task to track it here.</p>
  }
  const open = tasks.filter(entry => !entry.comment.task?.doneBy).sort(byCreated)
  const done = tasks.filter(entry => entry.comment.task?.doneBy).sort(byCreated)

  return (
    <div className={styles.list}>
      {open.length > 0 ? (
        <ul className={styles.items}>
          {open.map(entry => (
            <TaskItem key={entry.comment.id} {...entry} />
          ))}
        </ul>
      ) : null}
      {done.length > 0 ? (
        <>
          <button
            type="button"
            className={styles.doneToggle}
            aria-expanded={showDone}
            aria-controls={doneId}
            onClick={() => setShowDone(current => !current)}
          >
            Done ({done.length}){/* The expand pattern's chevron: down to open, up to close */}
            <span className={styles.chevron} aria-hidden="true">
              {showDone ? <ExpandLess /> : <ExpandMore />}
            </span>
          </button>
          <ul id={doneId} className={styles.items} hidden={!showDone}>
            {showDone ? done.map(entry => <TaskItem key={entry.comment.id} {...entry} />) : null}
          </ul>
        </>
      ) : null}
    </div>
  )
}
